import { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Boxes, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  Eye, Loader2, Mail, MapPin, MessageCircle, Minus, Package, Plus, Search,
  ShoppingBag, SlidersHorizontal, Trash2, Truck, Wrench, X,
} from "lucide-react";
import { useQuote } from "./QuoteModal";
import { ICONS, lineUnit, packageTotal, submitShopOrder, useShopData } from "../Libs/shopData";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/pages";
import { breadcrumbSchema } from "../seo/schema";

/* -------------------------------------------------------------------------- */
/*  SETTINGS                                                                  */
/* -------------------------------------------------------------------------- */

// The shop is a full-screen "stage". These keep it clear of your fixed
// navbar (top) and your mobile bottom tab bar (bottom). Adjust to match.
// Mobile: the stage covers the top navbar, so the product bar is the only thing at the top.
const STAGE_TOP = "pt-[env(safe-area-inset-top,0px)] lg:pt-[60px]"; // desktop navbar height
const STAGE_BOTTOM = "bottom-[76px] lg:bottom-0"; // mobile bottom tab bar height (stage stops above it)
const SIDE_TOP = "lg:top-[84px]"; // sticky order panel inside checkout

// The split-screen image starts at the very top of the screen, so the navbar
// tab hangs over it. Split width on desktop (info on the left, image on the right):
const IMG_W = "lg:w-[54%]";

const QUOTE_EQUIPMENT = "Pumps, panels and tanks";

/* -------------------------------------------------------------------------- */
/*  CATALOGUE  (loaded from Firestore by useShopData, edited in ShopManager)  */
/*  The page reads it through this context so every piece shares one copy.    */
/* -------------------------------------------------------------------------- */

const CatalogCtx = createContext(null);
const useCat = () => useContext(CatalogCtx);

const COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa",
  "Homa Bay", "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi",
  "Kirinyaga", "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu",
  "Machakos", "Makueni", "Mandera", "Marsabit", "Meru", "Migori", "Mombasa",
  "Murang'a", "Nairobi", "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
  "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River", "Tharaka-Nithi",
  "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot",
];

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "low", label: "Price: low to high" },
  { id: "high", label: "Price: high to low" },
  { id: "name", label: "Name A to Z" },
];

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "services", label: "Services", icon: Wrench },
  { id: "packages", label: "Packages", icon: Boxes },
];

const kes = (n) => `KES ${Math.round(n).toLocaleString("en-KE")}`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/* -------------------------------------------------------------------------- */
/*  CART  (unchanged: quantities and services, kept across refreshes)         */
/* -------------------------------------------------------------------------- */

const CART_KEY = "majimaji-cart-v3";
const EMPTY_CART = { items: {}, services: {}, packages: {} };

function loadCart({ productById, serviceById, packageById }) {
  try {
    const raw = JSON.parse(localStorage.getItem(CART_KEY) || "null");
    if (!raw) return EMPTY_CART;
    const items = Object.fromEntries(
      Object.entries(raw.items || {}).filter(([id, q]) => productById[id] && productById[id].inStock !== false && q > 0)
    );
    const services = Object.fromEntries(Object.keys(raw.services || {}).filter((id) => serviceById[id]).map((id) => [id, true]));
    const packages = Object.fromEntries(
      Object.entries(raw.packages || {})
        .filter(([id, v]) => packageById[id] && v && v.qty > 0)
        .map(([id, v]) => [id, { qty: Math.min(99, v.qty), install: v.install !== false }])
    );
    return { items, services, packages };
  } catch {
    return EMPTY_CART;
  }
}

function cartReducer(state, a) {
  switch (a.type) {
    case "add":
      return { ...state, items: { ...state.items, [a.id]: Math.min(99, (state.items[a.id] || 0) + 1) } };
    case "set": {
      const q = Math.max(0, Math.min(99, a.qty));
      const items = { ...state.items };
      if (q === 0) delete items[a.id];
      else items[a.id] = q;
      return { ...state, items };
    }
    case "remove": {
      const items = { ...state.items };
      delete items[a.id];
      return { ...state, items };
    }
    // A package stays one line in the order, so its own prices are kept.
    case "addPackage": {
      const cur = state.packages[a.id];
      return { ...state, packages: { ...state.packages, [a.id]: { qty: Math.min(99, (cur?.qty || 0) + 1), install: a.install } } };
    }
    case "setPackage": {
      const q = Math.max(0, Math.min(99, a.qty));
      const packages = { ...state.packages };
      if (q === 0) delete packages[a.id];
      else packages[a.id] = { ...packages[a.id], qty: q };
      return { ...state, packages };
    }
    case "removePackage": {
      const packages = { ...state.packages };
      delete packages[a.id];
      return { ...state, packages };
    }
    case "toggleService": {
      const services = { ...state.services };
      if (services[a.id]) delete services[a.id];
      else services[a.id] = true;
      return { ...state, services };
    }
    case "addService":
      return { ...state, services: { ...state.services, [a.id]: true } };
    case "removeService": {
      const services = { ...state.services };
      delete services[a.id];
      return { ...state, services };
    }
    case "clear":
      return EMPTY_CART;
    default:
      return state;
  }
}

function useCart(cat) {
  const { productById, serviceById, packageById } = cat;
  const [cart, dispatch] = useReducer(cartReducer, undefined, () => loadCart(cat));

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      /* storage unavailable: the cart just will not persist */
    }
  }, [cart]);

  const lines = useMemo(
    () => Object.entries(cart.items).map(([id, qty]) => ({ product: productById[id], qty, total: productById[id].price * qty })),
    [cart.items, productById]
  );
  const svcLines = useMemo(() => Object.keys(cart.services).map((id) => serviceById[id]), [cart.services, serviceById]);
  const pkgLines = useMemo(
    () =>
      Object.entries(cart.packages).map(([id, v]) => {
        const pkg = packageById[id];
        const unit = packageTotal(pkg, productById);
        return { pkg, qty: v.qty, install: v.install, unit, total: unit * v.qty };
      }),
    [cart.packages, packageById, productById]
  );
  const subtotal = lines.reduce((n, l) => n + l.total, 0) + pkgLines.reduce((n, l) => n + l.total, 0);
  const count = lines.reduce((n, l) => n + l.qty, 0) + svcLines.length + pkgLines.reduce((n, l) => n + l.qty, 0);

  const suggestions = useMemo(() => {
    const ids = new Set();
    lines.forEach((l) => {
      if (l.product.svc && serviceById[l.product.svc] && !cart.services[l.product.svc]) ids.add(l.product.svc);
    });
    return [...ids].map((id) => serviceById[id]);
  }, [lines, cart.services, serviceById]);

  return { cart, dispatch, lines, svcLines, pkgLines, subtotal, count, suggestions };
}

/* -------------------------------------------------------------------------- */
/*  ORDER REFERENCE  (the order itself is saved by submitShopOrder, which     */
/*  writes to orders/products|services|packages/quotations)                   */
/* -------------------------------------------------------------------------- */

function makeOrderRef() {
  const d = new Date();
  const stamp = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `MM-${stamp}-${Math.floor(1000 + Math.random() * 9000)}`;
}

/* -------------------------------------------------------------------------- */
/*  SMALL PIECES                                                              */
/* -------------------------------------------------------------------------- */

function useScrollLock(locked) {
  useEffect(() => {
    if (!locked) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

function useEscape(active, handler) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && handler();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, handler]);
}

function useIsDesktop() {
  const q = "(min-width: 1024px)";
  const [on, setOn] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setOn(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return on;
}

// Small light placeholder, used in the order summary.
function ProductImage({ product, iconSize = 40, tile = "h-16 w-16" }) {
  const { categoryById } = useCat();
  const Icon = ICONS[categoryById[product.cat]?.icon] || Package;
  if (product.img) {
    return <img src={product.img} alt={product.name} loading="lazy" className="h-full w-full object-cover" />;
  }
  return (
    <div role="img" aria-label={`${product.name} (photo coming soon)`} className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#E4EEF9] to-[#C9DDF3]">
      <span className={`flex ${tile} items-center justify-center rounded-2xl bg-white/80 text-[#0A3568]`}>
        <Icon size={iconSize} strokeWidth={1.6} />
      </span>
    </div>
  );
}

function QtyStepper({ qty, onChange, compact = false }) {
  const size = compact ? "h-8 w-8" : "h-10 w-10";
  const btn = `${size} flex items-center justify-center text-[#0A3568] transition-colors hover:bg-[#F3F7FB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F7BE0]`;
  return (
    <div className="inline-flex items-center rounded-xl border border-slate-300 bg-white">
      <button type="button" onClick={() => onChange(qty - 1)} aria-label="Decrease quantity" className={`${btn} rounded-l-xl`}>
        <Minus size={15} />
      </button>
      <span className="min-w-7 text-center text-sm font-bold tabular-nums text-[#0E1726]">{qty}</span>
      <button type="button" onClick={() => onChange(qty + 1)} aria-label="Increase quantity" className={`${btn} rounded-r-xl`}>
        <Plus size={15} />
      </button>
    </div>
  );
}

const inputClass = (err) =>
  `w-full rounded-xl border bg-white px-4 py-3.5 text-[#0E1726] outline-none transition focus:ring-2 ${
    err ? "border-red-400 focus:border-red-500 focus:ring-red-200" : "border-slate-300 focus:border-[#1F7BE0] focus:ring-[#1F7BE0]/25"
  }`;

function Field({ label, optional, error, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 flex items-baseline justify-between text-sm font-semibold text-slate-700">
        {label}
        {optional && <span className="font-normal text-slate-400">Optional</span>}
      </span>
      {children}
      {error && (
        <span data-error="true" role="alert" className="mt-1.5 block text-sm font-medium text-red-600">
          {error}
        </span>
      )}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*  STAGE PIECES  (the immersive shop)                                        */
/* -------------------------------------------------------------------------- */

const titleOf = (it) => it.name || it.label || it.title;
const iconOf = (tab, it, categoryById) => (tab === "products" ? ICONS[categoryById[it.cat]?.icon] || Package : tab === "services" ? ICONS[it.icon] || Wrench : Boxes);
const priceOf = (tab, it, productById) => (tab === "products" ? it.price : tab === "packages" ? packageTotal(it, productById) : null);

// Photo if the item has one, otherwise a deep-blue placeholder with its icon.
// big = the large stage image.
// Mobile: the image runs the full width, left edge to right edge, and shows whole
//         (a blurred copy of the photo fills any space above and below it).
// Desktop: it fills its half of the split screen edge to edge (object-cover).
function Visual({ item, Icon, big = false }) {
  if (item.img) {
    if (!big) return <img src={item.img} alt="" draggable={false} className="h-full w-full object-cover" />;
    return (
      <div className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#06213F]">
        <img src={item.img} alt="" aria-hidden="true" draggable={false} className="absolute inset-0 h-full w-full scale-125 object-cover opacity-40 blur-2xl lg:hidden" />
        <img src={item.img} alt={titleOf(item)} decoding="async" fetchpriority="high" draggable={false} className="relative h-auto max-h-full w-full object-cover object-center lg:h-full" />
      </div>
    );
  }
  return (
    <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-[#1A63B8] via-[#0F4C95] to-[#0A3568]">
      <Icon
        strokeWidth={big ? 1 : 1.5}
        className={
          big
            ? "absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 text-white/20 sm:h-64 sm:w-64 lg:h-96 lg:w-96"
            : "absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 text-white/80"
        }
      />
    </div>
  );
}

// compact = the collapsed rail (three cards share the width); otherwise the full scroller.
function RailCard({ item, tab, active, mark, soldOut, compact, onSelect, onQuick, cardRef }) {
  const { categoryById, productById } = useCat();
  const Icon = iconOf(tab, item, categoryById);
  const price = priceOf(tab, item, productById);
  const isAdded = tab === "services" && mark;
  return (
    <motion.div
      ref={cardRef}
      layout={compact ? "position" : false}
      initial={compact ? { opacity: 0, scale: 0.92 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 380, damping: 34 }}
      className={`relative rounded-2xl bg-white/10 p-1.5 ring-2 backdrop-blur-md transition-shadow ${
        compact ? "min-w-0 flex-1 sm:max-w-44" : "w-36 shrink-0 snap-center sm:w-44"
      } ${active ? "ring-[#F2A93B]" : "ring-transparent"}`}
    >
      <button type="button" onClick={onSelect} aria-label={`View ${titleOf(item)}`} aria-current={active} className="block w-full text-left focus-visible:outline-none">
        <div className={`overflow-hidden rounded-xl sm:h-24 ${compact ? "h-16" : "h-20"}`}>
          <Visual item={item} Icon={Icon} />
        </div>
        <div className={`px-1 pb-0.5 pt-2 ${compact ? "" : "pr-11"}`}>
          <p className="truncate text-xs font-bold sm:text-sm">{titleOf(item)}</p>
          <p className="truncate text-xs text-white/70">{soldOut ? "Out of stock" : price !== null ? kes(price) : "Quoted on site"}</p>
        </div>
      </button>
      {tab !== "services" && mark > 0 && (
        <span className="pointer-events-none absolute left-3 top-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F2A93B] px-1 text-[11px] font-extrabold text-[#0B1F3A]">{mark}</span>
      )}
      <button
        type="button"
        onClick={onQuick}
        disabled={soldOut}
        aria-label={isAdded ? `Remove ${titleOf(item)}` : `Add ${titleOf(item)} to order`}
        className={`absolute flex items-center justify-center rounded-full shadow-lg transition-transform active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white disabled:opacity-35 disabled:active:scale-100 ${
          compact ? "right-2.5 top-2.5 h-7 w-7" : "bottom-2 right-2 h-9 w-9"
        } ${isAdded ? "bg-[#1F7BE0] text-white" : "bg-[#F2A93B] text-[#0B1F3A]"}`}
      >
        {isAdded ? <Check size={compact ? 15 : 18} strokeWidth={3} /> : <Plus size={compact ? 15 : 18} strokeWidth={3} />}
      </button>
    </motion.div>
  );
}

function FilterCard({ onClose, query, setQuery, category, setCategory, sort, setSort, total, onClear }) {
  useEscape(true, onClose);
  const { categories } = useCat();
  const chip = (on) =>
    `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
      on ? "border-[#0A3568] bg-[#0A3568] text-white" : "border-slate-200 bg-white text-[#0E1726] hover:border-[#1F7BE0]/60"
    }`;
  return (
    <>
      <div className="fixed inset-0 -z-10 bg-[#031326]/50" onClick={onClose} />
      <div role="dialog" aria-label="Filter products" className="absolute right-3 top-full mt-1 w-[min(94vw,25rem)] rounded-3xl bg-white p-4 text-[#0E1726] shadow-2xl sm:right-6">
        <div className="relative">
          <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products"
            aria-label="Search products"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-3 outline-none transition focus:border-[#1F7BE0] focus:ring-2 focus:ring-[#1F7BE0]/25"
          />
        </div>
        <div role="group" aria-label="Categories" className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => setCategory("all")} aria-pressed={category === "all"} className={chip(category === "all")}>All</button>
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} aria-pressed={category === c.id} className={chip(category === c.id)}>
              {(() => { const I = ICONS[c.icon] || Package; return <I size={14} className={category === c.id ? "text-[#7DB8FF]" : "text-[#1F7BE0]"} />; })()}
              {c.label}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Sort" className="mt-3 flex flex-wrap gap-2">
          {SORTS.map((s) => (
            <button key={s.id} onClick={() => setSort(s.id)} aria-pressed={sort === s.id} className={chip(sort === s.id)}>{s.label}</button>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button onClick={onClear} className="rounded-xl px-4 py-2.5 text-sm font-bold text-[#0A3568] hover:bg-[#F3F7FB]">Clear</button>
          <button onClick={onClose} className="flex-1 rounded-xl bg-[#F2A93B] px-4 py-2.5 text-sm font-bold text-[#0B1F3A] hover:bg-[#0A3568] hover:text-white">
            Show {plural(total, "product", "products")}
          </button>
        </div>
      </div>
    </>
  );
}

function CartDrawer({ open, onClose, children }) {
  const reduce = useReducedMotion();
  const desktop = useIsDesktop();
  useScrollLock(open);
  useEscape(open, onClose);
  const off = reduce ? {} : desktop ? { x: "100%" } : { y: "100%" };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end bg-[#031326]/70 lg:items-stretch lg:justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Your order"
            onClick={(e) => e.stopPropagation()}
            initial={off}
            animate={{ x: 0, y: 0 }}
            exit={off}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="flex max-h-[88svh] w-full flex-col rounded-t-[28px] bg-white pb-[env(safe-area-inset-bottom,0px)] text-[#0E1726] lg:h-full lg:max-h-none lg:w-[420px] lg:rounded-none"
          >
            <div className="flex items-center justify-between px-5 pb-2 pt-5">
              <h2 className="font-heading text-xl font-extrabold">Your order</h2>
              <button onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full text-[#0A3568] hover:bg-[#F3F7FB]">
                <X size={22} />
              </button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------------------------------- */
/*  ORDER SUMMARY  (cart drawer and checkout both use this)                   */
/* -------------------------------------------------------------------------- */

function OrderSummary({ lines, svcLines, pkgLines = [], subtotal, suggestions = [], dispatch, readOnly = false, onCheckout }) {
  const { productById, serviceById } = useCat();
  const hasEquip = lines.length > 0 || pkgLines.length > 0;
  const empty = !hasEquip && svcLines.length === 0;
  const itemCount = lines.reduce((n, l) => n + l.qty, 0);

  if (empty) {
    return (
      <div className="flex flex-col items-center px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F3F7FB] text-[#1F7BE0]">
          <ShoppingBag size={26} />
        </span>
        <h3 className="font-heading mt-4 text-lg font-bold">Your order is empty</h3>
        <p className="mt-1.5 max-w-[16rem] text-sm text-slate-600">Add equipment, a service or a package and it will appear here with the price.</p>
      </div>
    );
  }

  const trash = "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5">
        {pkgLines.length > 0 && (
          <section aria-label="Packages">
            <h3 className="font-heading pt-4 text-xs font-bold text-slate-500">Packages ({pkgLines.reduce((n, l) => n + l.qty, 0)})</h3>
            <ul className="divide-y divide-slate-100">
              {pkgLines.map(({ pkg, qty, install, total }) => {
                const svc = serviceById[pkg.service];
                return (
                  <li key={pkg.id} className="py-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold leading-snug">{pkg.title}</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
                          {pkg.contents.map((c) => `${c.qty} × ${productById[c.id]?.name || "Item"}`).join(", ")}
                        </p>
                        {install && svc && <p className="mt-1 text-xs font-semibold text-[#1F7BE0]">Includes {svc.label.toLowerCase()}, priced after a site visit</p>}
                      </div>
                      {!readOnly && (
                        <button type="button" onClick={() => dispatch({ type: "removePackage", id: pkg.id })} aria-label={`Remove ${pkg.title}`} className={trash}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      {readOnly ? (
                        <span className="text-sm text-slate-500">Qty {qty}</span>
                      ) : (
                        <QtyStepper qty={qty} onChange={(q) => dispatch({ type: "setPackage", id: pkg.id, qty: q })} compact />
                      )}
                      <span className="text-sm font-bold text-[#0A3568]">{kes(total)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {lines.length > 0 && (
          <section aria-label="Equipment" className={pkgLines.length > 0 ? "border-t border-slate-100" : ""}>
            <h3 className="font-heading pt-4 text-xs font-bold text-slate-500">Equipment ({itemCount})</h3>
            <ul className="divide-y divide-slate-100">
              {lines.map(({ product, qty, total }) => (
                <li key={product.id} className="flex gap-3 py-4">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                    <ProductImage product={product} iconSize={20} tile="h-9 w-9" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-bold leading-snug">{product.name}</p>
                      {!readOnly && (
                        <button type="button" onClick={() => dispatch({ type: "remove", id: product.id })} aria-label={`Remove ${product.name}`} className={`${trash} -mr-1 -mt-1`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      {readOnly ? (
                        <span className="text-sm text-slate-500">Qty {qty}</span>
                      ) : (
                        <QtyStepper qty={qty} onChange={(q) => dispatch({ type: "set", id: product.id, qty: q })} compact />
                      )}
                      <span className="text-sm font-bold text-[#0A3568]">{kes(total)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {svcLines.length > 0 && (
          <section aria-label="Services" className={hasEquip ? "border-t border-slate-100" : ""}>
            <h3 className="font-heading pt-4 text-xs font-bold text-slate-500">Services ({svcLines.length})</h3>
            <ul className="divide-y divide-slate-100">
              {svcLines.map((s) => {
                const SIcon = ICONS[s.icon] || Wrench;
                return (
                  <li key={s.id} className="flex items-start gap-3 py-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F3F7FB] text-[#1F7BE0]">
                      <SIcon size={20} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold leading-snug">{s.label}</p>
                      <p className="mt-0.5 text-sm text-slate-500">Priced after a site visit</p>
                    </div>
                    {!readOnly && (
                      <button type="button" onClick={() => dispatch({ type: "removeService", id: s.id })} aria-label={`Remove ${s.label}`} className={trash}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {!readOnly && suggestions.length > 0 && (
          <section aria-label="Suggested installation" className="mb-4 rounded-2xl bg-[#F3F7FB] p-4">
            <p className="flex items-center gap-2 text-sm font-bold text-[#0A3568]">
              <Wrench size={15} />
              Want it installed too?
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button key={s.id} type="button" onClick={() => dispatch({ type: "addService", id: s.id })} className="inline-flex items-center gap-1.5 rounded-full border border-[#0A3568]/25 bg-white px-3 py-1.5 text-sm font-semibold text-[#0A3568] transition-colors hover:bg-[#0A3568] hover:text-white">
                  <Plus size={14} />
                  {s.label}
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-200 px-5 py-4">
        <dl className="space-y-1.5 text-sm">
          {hasEquip && (
            <div className="flex justify-between text-slate-600">
              <dt>Equipment and packages</dt>
              <dd className="font-semibold text-[#0E1726]">{kes(subtotal)}</dd>
            </div>
          )}
          {(svcLines.length > 0 || pkgLines.some((l) => l.install)) && (
            <div className="flex justify-between text-slate-600">
              <dt>Services</dt>
              <dd>Quoted after site visit</dd>
            </div>
          )}
          {hasEquip && (
            <div className="flex justify-between text-slate-600">
              <dt className="flex items-center gap-1.5"><Truck size={14} />Delivery</dt>
              <dd>Added to final order</dd>
            </div>
          )}
          <div className="flex items-baseline justify-between border-t border-slate-100 pt-2">
            <dt className="font-bold">Total so far</dt>
            <dd className="font-heading text-xl font-extrabold text-[#0A3568]">{hasEquip ? kes(subtotal) : "To be quoted"}</dd>
          </div>
        </dl>
        {!readOnly && (
          <>
            <button type="button" onClick={onCheckout} className="group mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F2A93B] px-6 py-3.5 font-bold text-[#0B1F3A] transition-colors hover:bg-[#0A3568] hover:text-white">
              Checkout
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="mt-2 text-center text-xs text-slate-500">No payment now. We send your final order first.</p>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  CHECKOUT  (Details, then Review)  (unchanged)                             */
/* -------------------------------------------------------------------------- */

const EMPTY_DETAILS = { name: "", phone: "", email: "", county: "", area: "", address: "", notes: "", contactVia: "whatsapp" };

function validate(d, hasEquipment) {
  const e = {};
  if (d.name.trim().length < 2) e.name = "Enter your name.";
  if (d.phone.replace(/\D/g, "").length < 9) e.phone = "Enter a phone number we can reach you on.";
  if (d.contactVia === "email" && !d.email.trim()) e.email = "Add your email so we can send your order there.";
  else if (d.email.trim() && !/^\S+@\S+\.\S+$/.test(d.email.trim())) e.email = "That email address does not look right.";
  if (!d.county) e.county = "Choose a county.";
  if (!d.area.trim()) e.area = "Enter your town or area.";
  if (!d.address.trim()) e.address = hasEquipment ? "Tell us where to deliver, with a landmark if you can." : "Tell us where the site is, with a landmark if you can.";
  return e;
}

function Checkout({ lines, svcLines, pkgLines, subtotal, dispatch, onBack, onPlaced }) {
  const { productById, serviceById } = useCat();
  const hasEquipment = lines.length > 0 || pkgLines.length > 0;
  const [step, setStep] = useState(0);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef(null);

  const set = (key) => (e) => {
    setDetails((d) => ({ ...d, [key]: e.target.value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const pickVia = (v) => {
    setDetails((d) => ({ ...d, contactVia: v }));
    setErrors((x) => ({ ...x, email: undefined }));
  };

  const toReview = (e) => {
    e.preventDefault();
    const found = validate(details, hasEquipment);
    setErrors(found);
    if (Object.keys(found).length === 0) {
      setStep(1);
      window.scrollTo({ top: 0 });
      return;
    }
    setTimeout(() => {
      formRef.current?.querySelector('[data-error="true"]')?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 50);
  };

  const place = async () => {
    if (status === "sending") return;
    setStatus("sending");
    setSubmitError("");
    const payload = {
      ref: makeOrderRef(),
      equipment: lines.map((l) => ({ id: l.product.id, name: l.product.name, qty: l.qty, price: l.product.price, total: l.total })),
      services: svcLines.map((s) => ({ id: s.id, label: s.label })),
      packages: pkgLines.map((l) => ({
        id: l.pkg.id,
        title: l.pkg.title,
        qty: l.qty,
        install: l.install && !!serviceById[l.pkg.service],
        installService: l.install ? serviceById[l.pkg.service]?.label || "" : "",
        unit: l.unit,
        total: l.total,
        contents: l.pkg.contents.map((c) => ({ id: c.id, name: productById[c.id]?.name || "", qty: c.qty, price: lineUnit(c, productById[c.id]) })),
      })),
      equipmentTotal: lines.reduce((n, l) => n + l.total, 0),
      packagesTotal: pkgLines.reduce((n, l) => n + l.total, 0),
      subtotal,
      details,
      placedAt: new Date().toISOString(),
    };
    try {
      await submitShopOrder(payload);
      onPlaced(payload);
    } catch {
      setStatus("idle");
      setSubmitError("We could not place your order. Check your connection and try again.");
    }
  };

  const viaTarget = details.contactVia === "whatsapp" ? details.phone.trim() || "your phone number" : details.email.trim() || "your email address";

  const summary = <OrderSummary lines={lines} svcLines={svcLines} pkgLines={pkgLines} subtotal={subtotal} dispatch={dispatch} readOnly />;

  return (
    <div>
      <button onClick={step === 0 ? onBack : () => setStep(0)} className="inline-flex items-center gap-2 text-sm font-bold text-[#0A3568] transition hover:text-[#1F7BE0]">
        <ArrowLeft size={18} />
        {step === 0 ? "Back to shop" : "Back to details"}
      </button>

      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-heading text-3xl font-extrabold leading-tight sm:text-4xl">{step === 0 ? "Your details" : "Review your order"}</h1>
        <ol className="flex items-center gap-2 text-sm" aria-label="Checkout steps">
          {["Details", "Review"].map((s, i) => (
            <li key={s} aria-current={i === step ? "step" : undefined} className="flex items-center gap-2">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-[#1F7BE0] text-white" : i === step ? "bg-[#0A3568] text-white" : "bg-slate-200 text-slate-500"}`}>
                {i < step ? <Check size={13} strokeWidth={3} /> : i + 1}
              </span>
              <span className={i === step ? "font-bold" : "text-slate-500"}>{s}</span>
              {i === 0 && <span className="mx-1 h-px w-6 bg-slate-300" />}
            </li>
          ))}
        </ol>
      </div>

      <details className="mt-6 rounded-2xl border border-slate-200 lg:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between px-5 py-4 font-bold [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-[#1F7BE0]" />
            Order summary
          </span>
          <span className="font-heading text-[#0A3568]">{hasEquipment ? kes(subtotal) : "To be quoted"}</span>
        </summary>
        <div className="flex flex-col border-t border-slate-100">{summary}</div>
      </details>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px] xl:gap-12">
        <div>
          {step === 0 ? (
            <form ref={formRef} onSubmit={toReview} noValidate className="space-y-10 rounded-3xl border border-slate-200 p-6 sm:p-8">
              <section>
                <h2 className="font-heading text-lg font-bold">Contact</h2>
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <Field label="Your name" error={errors.name}>
                    <input value={details.name} onChange={set("name")} autoComplete="name" className={inputClass(errors.name)} />
                  </Field>
                  <Field label="Phone number" error={errors.phone}>
                    <input type="tel" inputMode="tel" value={details.phone} onChange={set("phone")} autoComplete="tel" placeholder="07XX XXX XXX" className={inputClass(errors.phone)} />
                  </Field>
                  <Field label="Email" optional={details.contactVia !== "email"} error={errors.email} className="sm:col-span-2">
                    <input type="email" inputMode="email" value={details.email} onChange={set("email")} autoComplete="email" className={inputClass(errors.email)} />
                  </Field>
                </div>
              </section>

              <section>
                <h2 className="font-heading flex items-center gap-2 text-lg font-bold">
                  <MapPin size={18} className="text-[#1F7BE0]" />
                  {hasEquipment ? "Where should we deliver?" : "Where is the site?"}
                </h2>
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  <Field label="County" error={errors.county}>
                    <select value={details.county} onChange={set("county")} className={inputClass(errors.county)}>
                      <option value="">Choose a county</option>
                      {COUNTIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Town or area" error={errors.area}>
                    <input value={details.area} onChange={set("area")} autoComplete="address-level2" className={inputClass(errors.area)} />
                  </Field>
                  <Field label={hasEquipment ? "Delivery address or landmark" : "Site address or landmark"} error={errors.address} className="sm:col-span-2">
                    <textarea rows={3} value={details.address} onChange={set("address")} placeholder="Road, estate, plot number, or a landmark near the site" className={inputClass(errors.address)} />
                  </Field>
                  <Field label="Notes for us" optional className="sm:col-span-2">
                    <textarea rows={2} value={details.notes} onChange={set("notes")} placeholder="Gate access, best time to reach you, existing borehole or pump" className={inputClass(false)} />
                  </Field>
                </div>
              </section>

              <fieldset>
                <legend className="font-heading text-lg font-bold">How should we send your final order?</legend>
                <div role="radiogroup" aria-label="Where to send the final order" className="mt-4 grid gap-3 sm:grid-cols-2">
                  {[
                    { id: "whatsapp", label: "WhatsApp", hint: "To your phone number", icon: MessageCircle },
                    { id: "email", label: "Email", hint: "To your email address", icon: Mail },
                  ].map(({ id, label, hint, icon: Icon }) => {
                    const on = details.contactVia === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => pickVia(id)}
                        className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F7BE0] ${
                          on ? "border-[#0A3568] bg-[#0A3568]/[0.04]" : "border-slate-200 hover:border-[#1F7BE0]/50 hover:bg-[#F3F7FB]"
                        }`}
                      >
                        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${on ? "bg-[#0A3568] text-white" : "bg-[#F3F7FB] text-[#1F7BE0]"}`}>
                          <Icon size={22} />
                        </span>
                        <span>
                          <span className="block font-semibold">{label}</span>
                          <span className="block text-sm text-slate-500">{hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  We will send your final order to <span className="font-bold text-[#0E1726]">{viaTarget}</span>.
                </p>
              </fieldset>

              <button type="submit" className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition-colors hover:bg-[#0A3568] hover:text-white sm:w-auto">
                Review order
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          ) : (
            <div className="space-y-6 rounded-3xl border border-slate-200 p-6 sm:p-8">
              <section>
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-bold">{hasEquipment ? "Deliver to" : "Site"}</h2>
                  <button onClick={() => setStep(0)} className="text-sm font-bold text-[#1F7BE0] hover:underline">Edit</button>
                </div>
                <div className="mt-3 rounded-2xl bg-[#F3F7FB] p-4 text-sm leading-relaxed text-slate-700">
                  <p className="font-bold text-[#0E1726]">{details.name}</p>
                  <p>{details.phone}{details.email && ` · ${details.email}`}</p>
                  <p className="mt-2">{details.address}, {details.area}, {details.county}</p>
                  {details.notes && <p className="mt-2 text-slate-500">Note: {details.notes}</p>}
                </div>
              </section>

              <section className="rounded-2xl border-2 border-[#0A3568] p-5">
                <p className="flex items-center gap-2 font-heading font-bold text-[#0A3568]">
                  {details.contactVia === "whatsapp" ? <MessageCircle size={18} /> : <Mail size={18} />}
                  Your final order will be sent on {details.contactVia === "whatsapp" ? "WhatsApp" : "email"}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  To <span className="font-bold text-[#0E1726]">{viaTarget}</span>, within one working day.
                  {hasEquipment && " It will include your delivery cost."}
                  {(svcLines.length > 0 || pkgLines.some((l) => l.install)) && " It will include the price for your services after we assess the site."} You finalise payment from there, so you are not charged now.
                </p>
              </section>

              {submitError && <p role="alert" className="text-sm font-medium text-red-600">{submitError}</p>}

              <button onClick={place} disabled={status === "sending"} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#F2A93B] px-7 py-4 font-bold text-[#0B1F3A] transition-colors hover:bg-[#0A3568] hover:text-white disabled:pointer-events-none disabled:opacity-80 sm:w-auto">
                {status === "sending" ? (<><Loader2 size={18} className="animate-spin" />Placing order</>) : (<><Check size={18} />Place order</>)}
              </button>
            </div>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className={`sticky ${SIDE_TOP} flex max-h-[calc(100svh-108px)] flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white`}>
            <h2 className="font-heading px-5 pb-1 pt-5 text-lg font-extrabold">Order summary</h2>
            {summary}
          </div>
        </aside>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ORDER PLACED  (unchanged)                                                 */
/* -------------------------------------------------------------------------- */

function OrderPlaced({ order, onContinue }) {
  const { whatsapp: shopWhatsapp } = useCat();
  const reduce = useReducedMotion();
  const { details } = order;
  const whatsapp = details.contactVia === "whatsapp";
  const target = whatsapp ? details.phone : details.email;
  const hasEquipment = order.equipment.length > 0 || order.packages.length > 0;
  const hasServices = order.services.length > 0 || order.packages.some((k) => k.install);

  const text = [
    `Hello MajiMaji, I have placed order ${order.ref}.`,
    "",
    ...order.equipment.map((l) => `${l.qty} x ${l.name} (${kes(l.total)})`),
    ...order.packages.map((k) => `${k.qty} x Package: ${k.title} (${kes(k.total)})`),
    ...order.services.map((s) => `Service: ${s.label}`),
    "",
    hasEquipment ? `Equipment total: ${kes(order.subtotal)}` : "",
    `Location: ${details.address}, ${details.area}, ${details.county}`,
    `Name: ${details.name}`,
  ].filter((l, i, a) => l !== "" || a[i - 1] !== "").join("\n");

  const stepDot = "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0A3568] text-xs font-bold text-white";

  return (
    <div className="mx-auto max-w-2xl py-6 sm:py-10">
      <div className="flex flex-col items-center text-center">
        <motion.span
          initial={{ scale: reduce ? 1 : 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 18 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-[#1F7BE0] text-white"
        >
          <BadgeCheck size={40} />
        </motion.span>
        <h1 className="font-heading mt-7 text-3xl font-extrabold sm:text-4xl">Order received</h1>
        <p className="mt-3 max-w-lg text-lg leading-relaxed text-slate-600">
          Thank you, {details.name.trim().split(" ")[0]}. We will send your final order on{" "}
          <span className="font-bold text-[#0E1726]">{whatsapp ? "WhatsApp" : "email"}</span> to{" "}
          <span className="font-bold text-[#0E1726]">{target}</span> within one working day.
        </p>
        <p className="mt-5 rounded-xl bg-[#F3F7FB] px-4 py-2 text-sm text-slate-600">
          Order reference <span className="font-heading font-extrabold text-[#0A3568]">{order.ref}</span>
        </p>
      </div>

      <div className="mt-10 rounded-3xl border border-slate-200 p-6">
        <h2 className="font-heading text-lg font-bold">What happens next</h2>
        <ol className="mt-4 space-y-3 text-slate-700">
          <li className="flex gap-3">
            <span className={stepDot}>1</span>
            We check {hasEquipment ? "stock" : "your request"}{hasEquipment && " and work out delivery to your location"}{hasServices && ", and arrange a site visit for your services"}.
          </li>
          <li className="flex gap-3">
            <span className={stepDot}>2</span>
            Your final order arrives on {whatsapp ? "WhatsApp" : "email"} with the full price.
          </li>
          <li className="flex gap-3">
            <span className={stepDot}>3</span>
            You confirm it and finalise payment with us there.
          </li>
        </ol>
      </div>

      <div className="mt-6 rounded-3xl border border-slate-200 p-6">
        <h2 className="font-heading text-lg font-bold">Your order</h2>
        <ul className="mt-3 divide-y divide-slate-100 text-sm">
          {order.equipment.map((l) => (
            <li key={l.id} className="flex justify-between gap-4 py-2.5">
              <span><span className="font-bold">{l.qty} ×</span> {l.name}</span>
              <span className="shrink-0 font-semibold">{kes(l.total)}</span>
            </li>
          ))}
          {order.packages.map((k) => (
            <li key={k.id} className="flex justify-between gap-4 py-2.5">
              <span><span className="font-bold">{k.qty} ×</span> {k.title}{k.install && " + installation"}</span>
              <span className="shrink-0 font-semibold">{kes(k.total)}</span>
            </li>
          ))}
          {order.services.map((s) => (
            <li key={s.id} className="flex justify-between gap-4 py-2.5">
              <span>{s.label}</span>
              <span className="shrink-0 text-slate-500">Priced after site visit</span>
            </li>
          ))}
          {hasEquipment && (
            <li className="flex justify-between gap-4 py-3 font-bold">
              <span>Equipment total</span>
              <span className="text-[#0A3568]">{kes(order.subtotal)}</span>
            </li>
          )}
        </ul>
      </div>

      <div className="mt-8 flex flex-col items-center gap-4">
        <button onClick={onContinue} className="inline-flex items-center gap-2 rounded-xl bg-[#0A3568] px-8 py-4 font-bold text-white transition-colors hover:bg-[#1F7BE0]">
          Continue shopping
        </button>
        <a href={`https://wa.me/${shopWhatsapp}?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A3568] transition hover:text-[#1F7BE0]">
          <MessageCircle size={16} />
          Want to talk it through? Message us on WhatsApp
        </a>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  PAGE                                                                      */
/* -------------------------------------------------------------------------- */

function ShopView() {
  const { openQuote } = useQuote();
  const location = useLocation();
  const reduce = useReducedMotion();
  const desktop = useIsDesktop();
  const railRefs = useRef({});

  const cat = useCat();
  const { categories, products, services, packages, categoryById, productById, serviceById } = cat;
  const { cart, dispatch, lines, svcLines, pkgLines, subtotal, count, suggestions } = useCart(cat);

  const [view, setView] = useState("shop"); // shop | checkout | done
  const [tab, setTab] = useState("products");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false); // false = three items, true = full scroller
  const [menuHidden, setMenuHidden] = useState(true); // mobile only: menu starts hidden so only the product shows
  const [sheet, setSheet] = useState(false);
  const [toast, setToast] = useState(null);
  const [order, setOrder] = useState(null);
  const [activeIds, setActiveIds] = useState({});
  const [install, setInstall] = useState({}); // package id -> include installation (default true)
  const [viewer, setViewer] = useState(false); // image-only view
  useScrollLock(viewer);
  useEscape(viewer, () => setViewer(false));

  // Links like /shop#pumps, /shop#services or /shop#packages
  useEffect(() => {
    const id = location.hash.replace("#", "");
    if (categoryById[id]) {
      setTab("products");
      setCategory(id);
    } else if (id === "services" || id === "packages") {
      setTab(id);
    }
  }, [location.hash]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [view]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = products.filter(
      (p) =>
        (category === "all" || p.cat === category) &&
        (!q || p.name.toLowerCase().includes(q) || p.spec.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q)))
    );
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [category, query, sort, products]);

  const items = tab === "products" ? results : tab === "services" ? services : packages;
  const idx = Math.max(0, items.findIndex((i) => i.id === activeIds[tab]));
  const active = items[idx];
  const ActiveIcon = active ? iconOf(tab, active, categoryById) : Package;

  const select = (id) => setActiveIds((a) => ({ ...a, [tab]: id }));
  const go = (d) => {
    if (items.length < 2) return;
    select(items[(idx + d + items.length) % items.length].id);
  };

  // keep the selected card centred in the rail
  useEffect(() => {
    if (view !== "shop" || !active || !(desktop ? railOpen : !menuHidden)) return;
    railRefs.current[active.id]?.scrollIntoView({ inline: "center", block: "nearest", behavior: reduce ? "auto" : "smooth" });
  }, [active?.id, tab, view, reduce, railOpen, desktop, menuHidden]);

  // left / right arrow keys flip through items on desktop
  useEffect(() => {
    if (view !== "shop" || sheet || filtersOpen) return;
    const onKey = (e) => {
      if (/INPUT|SELECT|TEXTAREA/.test(e.target.tagName)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const changeTab = (id) => {
    setTab(id);
    setFiltersOpen(false);
    setRailOpen(false);
    setMenuHidden(true);
  };

  const addProduct = (id) => {
    dispatch({ type: "add", id });
    setToast(`Added ${productById[id].name}`);
  };
  const toggleService = (id) => {
    if (!cart.services[id]) setToast(`Added ${serviceById[id].label}`);
    dispatch({ type: "toggleService", id });
  };
  const addPackage = (pkg) => {
    dispatch({ type: "addPackage", id: pkg.id, install: install[pkg.id] !== false && !!serviceById[pkg.service] });
    setToast(`Added ${pkg.title}`);
  };
  const available = (t, it) =>
    t === "products" ? it.inStock !== false : t === "packages" ? it.contents.every((c) => productById[c.id] && productById[c.id].inStock !== false) : true;
  const quick = (it) => {
    if (!available(tab, it)) return;
    if (tab === "products") addProduct(it.id);
    else if (tab === "services") toggleService(it.id);
    else addPackage(it);
  };
  const quote = (service) => openQuote({ services: [service] });
  const goCheckout = () => {
    setSheet(false);
    setView("checkout");
  };
  const clearFilters = () => {
    setQuery("");
    setCategory("all");
    setSort("featured");
  };

  const qty = active && tab === "products" ? cart.items[active.id] || 0 : 0;
  const svcOn = active && tab === "services" ? !!cart.services[active.id] : false;
  const soldOut = !!active && !available(tab, active);
  const filterCount = (category !== "all" ? 1 : 0) + (query.trim() ? 1 : 0) + (sort !== "featured" ? 1 : 0);

  // The price is already shown in the item info, so the button never repeats it.
  const primaryLabel = !active
    ? ""
    : soldOut
    ? tab === "products" ? "Out of stock" : "Unavailable right now"
    : tab === "services"
    ? svcOn ? "Added. Tap to remove" : "Add service"
    : "Add to order";
  const PrimaryIcon = svcOn ? Check : Plus;
  const primaryCls = `inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-bold transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${
    svcOn ? "bg-[#1F7BE0] text-white" : "bg-[#F2A93B] text-[#0B1F3A] hover:bg-white"
  }`;

  const orderPanel = (
    <OrderSummary lines={lines} svcLines={svcLines} pkgLines={pkgLines} subtotal={subtotal} suggestions={suggestions} dispatch={dispatch} onCheckout={goCheckout} />
  );

  const cartLabel = subtotal > 0 ? kes(subtotal) : count > 0 ? "To be quoted" : "Empty";

  const emptyBlock = (
    <div className="pointer-events-auto max-w-md">
      <h2 className="font-heading text-3xl font-extrabold">{tab === "products" && filterCount > 0 ? "Nothing matches that" : "Nothing here yet"}</h2>
      <p className="mt-2 text-white/75">{tab === "products" && filterCount > 0 ? "Try another word, or tell us what you need and we will quote it." : "Tell us what you need and we will quote it."}</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {filterCount > 0 && <button onClick={clearFilters} className="rounded-2xl bg-white/15 px-5 py-3 font-bold backdrop-blur hover:bg-white/25">Clear filters</button>}
        <button onClick={() => quote(QUOTE_EQUIPMENT)} className="rounded-2xl bg-[#F2A93B] px-5 py-3 font-bold text-[#0B1F3A] hover:bg-white">Ask for a quote</button>
      </div>
    </div>
  );

  const h2Cls = "font-heading text-2xl font-extrabold leading-[1.1] sm:text-4xl lg:text-5xl xl:text-6xl";

  // Desktop actions. They sit beside the price (or under the summary for services),
  // wrap inside the left section, and keep the quantity stepper in the same row.
  const desktopActions = active && (
    <div className="hidden flex-wrap items-center gap-3 lg:flex">
      <button type="button" disabled={soldOut} onClick={() => quick(active)} className={primaryCls}>
        <PrimaryIcon size={18} strokeWidth={3} />
        {primaryLabel}
      </button>
      {tab === "products" && qty > 0 && <QtyStepper qty={qty} onChange={(q) => dispatch({ type: "set", id: active.id, qty: q })} />}
      {tab === "packages" && cart.packages[active.id]?.qty > 0 && (
        <QtyStepper qty={cart.packages[active.id].qty} onChange={(q) => dispatch({ type: "setPackage", id: active.id, qty: q })} />
      )}
      {tab === "products" && !soldOut && (
        <button
          type="button"
          onClick={() => {
            if (!qty) dispatch({ type: "add", id: active.id });
            goCheckout();
          }}
          className="rounded-2xl bg-white/15 px-6 py-3.5 font-bold backdrop-blur hover:bg-white/25"
        >
          Buy now
        </button>
      )}
    </div>
  );

  // The selected item's full content. Used by the desktop panel and the mobile scroller.
  const info = active && (
    <>
      {tab === "products" && (
        <>
          <p className="text-sm font-semibold text-[#9CCBFF]">{categoryById[active.cat]?.label}</p>
          <h2 className={`${h2Cls} mt-1.5`}>{active.name}</h2>
          <p className="mt-2 text-base font-semibold text-white/90">{active.spec}</p>
          <p className={`mt-1.5 max-w-md text-sm text-white/75 lg:line-clamp-none lg:mt-2 lg:text-base ${menuHidden ? "" : "line-clamp-2"}`}>{active.desc}</p>
          <div className={`mt-3 flex-wrap gap-2 lg:flex ${menuHidden ? "flex" : "hidden"}`}>
            {active.tags.map((t) => (
              <span key={t} className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">{t}</span>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-3 lg:mt-5">
            <p className="font-heading text-2xl font-extrabold sm:text-4xl">{kes(active.price)}</p>
            {desktopActions}
          </div>
        </>
      )}

      {tab === "services" && (
        <>
          <h2 className={h2Cls}>{active.label}</h2>
          <p className="mt-3 max-w-md text-base text-white/80">{active.summary}</p>
          <ul className={`mt-4 space-y-2 sm:block ${menuHidden ? "block" : "hidden"}`}>
            {active.points.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm text-white/90">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1F7BE0]"><Check size={12} strokeWidth={3} /></span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
            <p className="inline-block rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold backdrop-blur">Priced after a site visit</p>
            {desktopActions}
          </div>
          <div>
            <button onClick={() => quote(active.label)} className="mt-3 text-sm font-bold text-[#9CCBFF] underline-offset-4 hover:underline">Prefer the full quote form?</button>
          </div>
        </>
      )}

      {tab === "packages" && (
        <>
          <h2 className={h2Cls}>{active.title}</h2>
          <p className="mt-3 max-w-md text-base text-white/80">{active.blurb}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {active.contents.map((c) => (
              <span key={c.id} className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                {c.qty} × {productById[c.id]?.name || "Item"}
              </span>
            ))}
          </div>
          {serviceById[active.service] && (
          <button
            type="button"
            role="switch"
            aria-checked={install[active.id] !== false}
            onClick={() => setInstall((st) => ({ ...st, [active.id]: st[active.id] === false }))}
            className="mt-4 inline-flex items-center gap-3 rounded-full bg-white/15 py-2 pl-3 pr-4 text-sm font-semibold backdrop-blur hover:bg-white/25"
          >
            <span className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${install[active.id] !== false ? "bg-[#F2A93B]" : "bg-white/30"}`}>
              <span className={`h-4 w-4 rounded-full bg-white transition-transform ${install[active.id] !== false ? "translate-x-4" : ""}`} />
            </span>
            Include {serviceById[active.service].label.toLowerCase()}
          </button>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 lg:mt-4">
            <p className="font-heading text-2xl font-extrabold sm:text-4xl">{kes(packageTotal(active, productById))}</p>
            {desktopActions}
          </div>
          <p className="mt-1 text-sm text-white/70">Equipment total. Installation is priced after a site visit.</p>
        </>
      )}
    </>
  );

  /* ---------------------- checkout and confirmation ---------------------- */
  if (view !== "shop") {
    return (
      <div className="bg-white text-[#0E1726]">
        <div className="mx-auto max-w-7xl px-5 pb-40 pt-24 sm:px-8 lg:pb-20 lg:pt-28">
          {view === "checkout" && (
            <Checkout
              lines={lines}
              svcLines={svcLines}
              pkgLines={pkgLines}
              subtotal={subtotal}
              dispatch={dispatch}
              onBack={() => setView("shop")}
              onPlaced={(placed) => {
                setOrder(placed);
                dispatch({ type: "clear" });
                setView("done");
              }}
            />
          )}
          {view === "done" && order && (
            <OrderPlaced
              order={order}
              onContinue={() => {
                setOrder(null);
                setView("shop");
              }}
            />
          )}
        </div>
        <footer className="bg-[#041A33] px-5 pb-32 pt-8 text-sm text-white/60 sm:px-8 lg:pb-8">
          <div className="mx-auto max-w-7xl">&copy; {new Date().getFullYear()} MajiMaji Hub. All rights reserved.</div>
        </footer>
      </div>
    );
  }

  /* ------------------------------ the stage ------------------------------ */
  // Collapsed rail: the selected item with one neighbour each side (wraps like the arrows).
  // Choosing any item from the full scroller drops back to this three-item view.
  const menuVisible = desktop || !menuHidden; // desktop always shows the menu
  const n = items.length;
  const canCollapse = n > 3;
  // Desktop: three items + "More" button. Mobile: no more-button; the menu is the full scroller, shown or hidden.
  const expanded = desktop ? railOpen && canCollapse : true;
  const shown = canCollapse ? [-1, 0, 1].map((o) => items[(idx + o + n) % n]) : items;
  const renderCard = (it, compact) => (
    <RailCard
      key={it.id}
      item={it}
      tab={tab}
      compact={compact}
      active={active?.id === it.id}
      mark={tab === "products" ? cart.items[it.id] || 0 : tab === "services" ? !!cart.services[it.id] : cart.packages[it.id]?.qty || 0}
      soldOut={!available(tab, it)}
      onSelect={() => {
        select(it.id);
        setRailOpen(false);
        setMenuHidden(true); // mobile: choosing an item closes the menu
      }}
      onQuick={() => quick(it)}
      cardRef={(el) => (railRefs.current[it.id] = el)}
    />
  );

  return (
    <>
      <div className={`fixed inset-x-0 top-0 z-[60] flex flex-col overflow-hidden bg-[#031326] text-white lg:z-40 ${STAGE_TOP} ${STAGE_BOTTOM}`}>
        <h1 className="sr-only">Water pumps, solar panels and tanks: MajiMaji Hub shop</h1>

        {/* soft glow behind the text. It sits under the image, never on it. */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_70%,rgba(15,76,149,0.35),transparent_60%)]" />

        {/* top bar: sections and one compact filter card */}
        <div className="relative z-30 order-1 flex items-center gap-2 px-3 py-3 sm:px-6">
          <div role="tablist" aria-label="Shop sections" className="grid flex-1 grid-cols-3 gap-1 rounded-2xl bg-[#031326]/55 p-1 backdrop-blur-md lg:max-w-md lg:flex-none lg:basis-[28rem]">
            {TABS.map((t) => {
              const on = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={on}
                  onClick={() => changeTab(t.id)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-2 py-2.5 text-sm font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${on ? "bg-white text-[#0A3568]" : "text-white/80 hover:bg-white/10"}`}
                >
                  <t.icon size={16} className="hidden min-[400px]:block" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="hidden flex-1 lg:block" />

          {tab === "products" && (
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label="Filters"
              className="relative flex h-11 items-center gap-2 rounded-2xl bg-[#031326]/55 px-3.5 text-sm font-bold backdrop-blur-md hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              <SlidersHorizontal size={18} />
              <span className="hidden sm:inline">Filters</span>
              {filterCount > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F2A93B] px-1 text-xs font-extrabold text-[#0B1F3A]">{filterCount}</span>}
            </button>
          )}

          <button type="button" onClick={() => setSheet(true)} aria-label="Open your order" className="hidden h-11 items-center gap-3 rounded-2xl bg-[#F2A93B] px-4 font-bold text-[#0B1F3A] hover:bg-white lg:flex">
            <ShoppingBag size={18} />
            <span>{cartLabel}</span>
            {count > 0 && <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#0B1F3A] px-1.5 text-xs font-extrabold text-white">{count}</span>}
          </button>

          {filtersOpen && tab === "products" && (
            <FilterCard
              onClose={() => setFiltersOpen(false)}
              query={query}
              setQuery={setQuery}
              category={category}
              setCategory={setCategory}
              sort={sort}
              setSort={setSort}
              total={results.length}
              onClear={clearFilters}
            />
          )}
        </div>

        {/* the image. Mobile: it takes the space between the section tabs and the
               details and runs the full width, left to right. Desktop: the right half. */}
        <div className={`relative order-2 min-h-[22svh] flex-1 lg:absolute lg:inset-y-0 lg:right-0 lg:min-h-0 lg:flex-none ${IMG_W}`}>
          <AnimatePresence initial={false}>
            {active && (
              <motion.div
                key={`${tab}-${active.id}`}
                className="absolute inset-0"
                initial={{ opacity: 0, scale: reduce ? 1 : 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reduce ? 0 : 0.6, ease: "easeOut" }}
              >
                <Visual item={active} Icon={ActiveIcon} big />
              </motion.div>
            )}
          </AnimatePresence>
          {/* swipe on the image to flip to the next item */}
          <motion.div
            className="absolute inset-0 z-[1]"
            drag="x"
            dragSnapToOrigin
            dragElastic={0.15}
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) go(1);
              else if (info.offset.x > 60) go(-1);
            }}
          />
          {/* translucent eye: opens the image on its own */}
          {active?.img && (
            <button
              type="button"
              onClick={() => setViewer(true)}
              aria-label="View image only"
              className="absolute bottom-3 right-3 z-[3] flex h-11 w-11 items-center justify-center rounded-full bg-[#031326]/45 text-white backdrop-blur-md transition hover:bg-[#031326]/70 active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white lg:bottom-auto lg:right-6 lg:top-1/2 lg:-translate-y-1/2"
            >
              <Eye size={22} />
            </button>
          )}
        </div>

        {/* details sit on the left, over the soft glow, always below the bar */}
        <div className="pointer-events-none relative z-10 order-3 flex min-h-0 flex-none items-end justify-between gap-6 px-4 pb-3 pt-3 sm:px-8 lg:flex-1 lg:pb-6 lg:pt-0 lg:pl-14">
          {!active ? (
            emptyBlock
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={`${tab}-${active.id}`}
                initial={{ opacity: 0, y: reduce ? 0 : 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22 }}
                className="pointer-events-auto max-h-[34svh] min-w-0 max-w-lg overflow-y-auto lg:max-h-full lg:max-w-xl pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {info}
              </motion.div>
            </AnimatePresence>
          )}

          {items.length > 1 && (
            <div className="pointer-events-auto hidden shrink-0 items-center gap-3 lg:flex">
              <span className="text-sm font-semibold tabular-nums text-white/70">{idx + 1} / {items.length}</span>
              <button onClick={() => go(-1)} aria-label="Previous item" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 backdrop-blur hover:bg-white/30"><ChevronLeft size={22} /></button>
              <button onClick={() => go(1)} aria-label="Next item" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/15 backdrop-blur hover:bg-white/30"><ChevronRight size={22} /></button>
            </div>
          )}
        </div>

        {/* bottom: sideways rail with quick add, plus the always-there mobile bar */}
        {items.length > 0 && (
          <div className="relative z-20 order-4">
            {/* mobile only: chevron handle to hide / show the whole menu */}
            <div className="flex justify-center pb-1.5 lg:hidden">
              <button
                type="button"
                onClick={() => setMenuHidden((h) => !h)}
                aria-expanded={!menuHidden}
                aria-label={menuHidden ? "Show the product menu" : "Hide the product menu"}
                className="flex h-8 items-center gap-1.5 rounded-full bg-white/15 pl-3 pr-4 text-xs font-bold backdrop-blur-md active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                {menuHidden ? <ChevronUp size={18} strokeWidth={3} /> : <ChevronDown size={18} strokeWidth={3} />}
                {menuHidden ? `More ${tab}` : "Hide"}
              </button>
            </div>

            <AnimatePresence initial={false}>
              {menuVisible && (
                <motion.div
                  key="menu"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reduce ? 0 : 0.3, ease: "easeOut" }}
                  className="overflow-hidden"
                >
            {expanded ? (
              <div>
                <div className="hidden items-center justify-between px-4 pb-1.5 sm:px-8 lg:flex lg:px-14">
                  <p className="text-sm font-bold text-white/80">All {n} {tab}</p>
                  <button
                    type="button"
                    onClick={() => setRailOpen(false)}
                    aria-expanded="true"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-sm font-bold backdrop-blur-md hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                  >
                    <ChevronDown size={16} strokeWidth={3} />
                    Show less
                  </button>
                </div>
                <div role="listbox" aria-label="Choose an item" className="flex snap-x gap-3 overflow-x-auto scroll-px-4 px-4 pb-2 pt-1 sm:px-8 lg:px-14 lg:pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {items.map((it) => renderCard(it, false))}
                </div>
              </div>
            ) : (
              <div role="listbox" aria-label="Choose an item" className="flex gap-2 px-4 pb-2 pt-1 sm:gap-3 sm:px-8 lg:px-14 lg:pb-5">
                {shown.map((it) => renderCard(it, true))}
                {canCollapse && (
                  <button
                    type="button"
                    onClick={() => setRailOpen(true)}
                    aria-expanded="false"
                    aria-label={`Show more ${tab}`}
                    className="flex w-[76px] shrink-0 flex-col items-center justify-center gap-1 rounded-2xl bg-[#F2A93B] px-2 text-[#0B1F3A] shadow-lg transition-transform hover:bg-white active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white sm:w-28"
                  >
                    <span className="text-center text-xs font-extrabold leading-tight sm:text-sm">More {tab}</span>
                    <ChevronRight size={22} strokeWidth={3} />
                  </button>
                )}
              </div>
            )}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-2 bg-[#031326]/90 px-3 pb-3 pt-2 backdrop-blur-md lg:hidden">
              <button type="button" disabled={soldOut} onClick={() => active && quick(active)} className={`${primaryCls} min-w-0 flex-1 text-sm sm:text-base`}>
                <PrimaryIcon size={18} strokeWidth={3} className="shrink-0" />
                <span className="truncate">{primaryLabel}</span>
              </button>
              <button type="button" onClick={() => setSheet(true)} aria-label={`Open your order, ${count} items`} className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl bg-white text-[#0A3568]">
                <ShoppingBag size={22} />
                {count > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#F2A93B] px-1 text-xs font-extrabold text-[#0B1F3A]">{count}</span>}
              </button>
            </div>
          </div>
        )}
      </div>

      <CartDrawer open={sheet} onClose={() => setSheet(false)}>
        {orderPanel}
      </CartDrawer>

      {/* image-only viewer */}
      <AnimatePresence>
        {viewer && active?.img && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#020B16]/95 p-3 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setViewer(false)}
            role="dialog"
            aria-modal="true"
            aria-label={`${titleOf(active)} image`}
          >
            <img src={active.img} alt={titleOf(active)} draggable={false} className="max-h-full max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
            <button
              type="button"
              onClick={() => setViewer(false)}
              aria-label="Close image"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              <X size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* "added" confirmation */}
      <div className="pointer-events-none fixed inset-x-0 top-32 z-[60] flex justify-center px-4" aria-live="polite">
        <AnimatePresence>
          {toast && (
            <motion.div key={toast} initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#0A3568] shadow-xl">
              <Check size={16} className="text-[#1F7BE0]" strokeWidth={3} />
              {toast}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  ENTRY  (waits for the database, then hands the catalogue to the page)     */
/* -------------------------------------------------------------------------- */

export default function Shop() {
  const { shop, ready } = useShopData();

  const value = useMemo(
    () => ({
      ...shop,
      categoryById: Object.fromEntries(shop.categories.map((c) => [c.id, c])),
      productById: Object.fromEntries(shop.products.map((p) => [p.id, p])),
      serviceById: Object.fromEntries(shop.services.map((x) => [x.id, x])),
      packageById: Object.fromEntries(shop.packages.map((k) => [k.id, k])),
    }),
    [shop]
  );

  const seo = (
    <Seo
      {...PAGE_SEO.shop}
      path="/shop"
      schema={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Shop", path: "/shop" },
      ])}
    />
  );

  if (!ready) {
    return (
      <>
        {seo}
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#031326] text-white">
          <Loader2 className="animate-spin" size={28} aria-label="Loading the shop" />
        </div>
      </>
    );
  }
  return (
    <CatalogCtx.Provider value={value}>
      {seo}
      <ShopView />
    </CatalogCtx.Provider>
  );
}